// src/components/KbAdminPanel.tsx
import { useEffect } from "react";
import { getAdminToken } from "../lib/kbAdminClient";
import { useAuthOptional } from "@/contexts/AuthContext";
import { useOrgSettings } from "@/hooks/useOrgSettings";
import { useSpeechDictation } from "@/hooks/useSpeechDictation";
import { KbAdminWorkspaceChrome } from "./kb-admin/KbAdminWorkspaceChrome";
import { useKbGapInboxWorkflow } from "./kb-admin/useKbGapInboxWorkflow";
import { useKbTrashAndPublishWorkflow } from "./kb-admin/useKbTrashAndPublishWorkflow";
import { useKbAiSessionHelpers } from "./kb-admin/useKbAiSessionHelpers";
import { useKbAdminState } from "./kb-admin/useKbAdminState";
import { useKbLibraryWorkflow } from "./kb-admin/useKbLibraryWorkflow";
import { useKbGapInboxData } from "./kb-admin/useKbGapInboxData";
import { useKbEditorWorkflow } from "./kb-admin/useKbEditorWorkflow";
import { useKbArticleSaveWorkflow } from "./kb-admin/useKbArticleSaveWorkflow";
import { useKbImportAndSplitWorkflow } from "./kb-admin/useKbImportAndSplitWorkflow";
import { useKbAiHelpWorkflow } from "./kb-admin/useKbAiHelpWorkflow";
import { KbAdminModes } from "./kb-admin/KbAdminModes";
import { buildKbAdminModesProps } from "./kb-admin/kbAdminModesProps";
import { useKbAdminPanelMeta } from "./kb-admin/useKbAdminPanelMeta";
import { useKbAdminPanelEffects } from "./kb-admin/useKbAdminPanelEffects";
import { resolveKbAccess } from "./kb-admin/kbAdminAccess";
import { type PanelNote } from "./kb-admin/types";

const STARTER_COMPANY_DOCS_URL = "/kb/company-doc-starter.json";

export function KbAdminPanel(props: {
  orgId?: string | null;
  onLogout?: () => void;
  /** Switch to Dev tab and scroll to KB rescope (platform_dev workflow). */
  onNavigateToDevKbRescope?: () => void;
  /** When set to gap_inbox, opens that sub-mode once (e.g. from dashboard alert). */
  entrySubModeIntent?: "default" | "gap_inbox";
  onEntrySubModeIntentConsumed?: () => void;
}) {
  const auth = useAuthOptional();
  const orgSettings = useOrgSettings();
  const dictation = useSpeechDictation();
  const hasLegacyAdminToken = !!getAdminToken();
  const {
    canPublishKb,
    canManageAppScope,
    canManageManufacturerScope,
    canUseQuickChunkAudit,
    showDevRescopePill,
    hasKbAccess,
    defaultWriteScope,
  } = resolveKbAccess({
    hasSession: !!auth?.session,
    hasRole: (...roles: string[]) => auth?.hasRole(...roles) ?? false,
    hasLegacyAdminToken,
  });

  const {
    q, setQ, results, setResults, selected, setSelected, note, setNote, notesOpen, setNotesOpen, articleTitle, setArticleTitle,
    articleContent, setArticleContent, articleMode, setArticleMode, lastArticleId, setLastArticleId, articleSlug, setArticleSlug, loadedArticle, setLoadedArticle,
    articleAudience, setArticleAudience, articleScope, setArticleScope, articleDocType, setArticleDocType, articleTagsInput, setArticleTagsInput, articleSourceUrl, setArticleSourceUrl,
    articleStatus, setArticleStatus, searchAudience, setSearchAudience, searchScope, setSearchScope, searchDocType, setSearchDocType, searchStatus, setSearchStatus,
    quickAuditEnabled, setQuickAuditEnabled, quickAuditRisk, setQuickAuditRisk, chunkQuality, setChunkQuality, chunkQualityAdvice, setChunkQualityAdvice, bulkImportText,
    setBulkImportText, isBulkImporting, setIsBulkImporting, devRescopeBusy, setDevRescopeBusy, draftCount, setDraftCount, draftsLoading, setDraftsLoading, aiHelpOpen, setAiHelpOpen,
    aiPurpose, setAiPurpose, aiAudienceHint, setAiAudienceHint, aiScopeHint, setAiScopeHint, aiDocTypeHint, setAiDocTypeHint, aiSourceKind, setAiSourceKind, aiBusy, setAiBusy,
    aiGeneratedNotes, setAiGeneratedNotes, appliedSuggestion, setAppliedSuggestion, aiFeedback, setAiFeedback, aiResolvedFacts, setAiResolvedFacts, aiPlacementSummary, setAiPlacementSummary,
    aiSuggestedAction, setAiSuggestedAction, aiTopicComplexity, setAiTopicComplexity, aiRecommendedDocCount, setAiRecommendedDocCount, aiStopReason, setAiStopReason, aiFollowOnDocs,
    setAiFollowOnDocs, aiKbGapCandidates, setAiKbGapCandidates, aiArticleLinkSuggestions, setAiArticleLinkSuggestions, aiRelatedArticles, setAiRelatedArticles,
    aiQuestionAnswers, setAiQuestionAnswers, aiBodyDraft, setAiBodyDraft, aiBodyGenerationNotes, setAiBodyGenerationNotes, previousArticleContent, setPreviousArticleContent,
    preserveOriginalBody, setPreserveOriginalBody, aiUpdateTitle, setAiUpdateTitle, aiUpdateMetadata, setAiUpdateMetadata, relatedPreviewLoading, setRelatedPreviewLoading,
    relatedPreview, setRelatedPreview, aiLinksOpen, setAiLinksOpen, aiGapsOpen, setAiGapsOpen, splitProposals, setSplitProposals, splitWorking, setSplitWorking, splitCreating,
    setSplitCreating, splitOpen, setSplitOpen, gapInboxItems, setGapInboxItems, gapInboxOpenCount, setGapInboxOpenCount, gapInboxLoading, setGapInboxLoading, gapSaving, setGapSaving,
    gapInboxStatusFilter, setGapInboxStatusFilter, gapInboxPageSize, setGapInboxPageSize, gapInboxPage, setGapInboxPage, gapInboxListTotal, setGapInboxListTotal, gapInboxTestingOnly,
    setGapInboxTestingOnly, kbMode, setKbMode, pendingProposalCount, setPendingProposalCount, bulkAllowPolicyOverwrite, setBulkAllowPolicyOverwrite, confirmDeleteSlug,
    setConfirmDeleteSlug, deleteWorking, setDeleteWorking, confirmPublishSlug, setConfirmPublishSlug, publishWorking, setPublishWorking, trashResults, setTrashResults, trashLoading,
    setTrashLoading, confirmPurgeSlug, setConfirmPurgeSlug,
  } = useKbAdminState(canPublishKb);

  const { editorName, hasEditorName, publishedArticleLocked, broaderScopeLocked, kbOrgDisplayName, defaultArticleTitle, aiGuidance } =
    useKbAdminPanelMeta({ orgId: props.orgId, auth, orgSettings, canPublishKb, canManageAppScope, canManageManufacturerScope, loadedArticle });

  useEffect(() => {
    // Reset split panel and AI editor open state when switching modes
    setSplitProposals([]);
    setSplitOpen(false);
    setAiHelpOpen(false);
    setAiLinksOpen(false);
    setAiGapsOpen(false);
  }, [kbMode]);

  function showNote(next: PanelNote | null) {
    setNote(next);
  }

  function showAiFeedback(next: PanelNote | null) {
    setAiFeedback(next);
    if (next) {
      setSplitOpen(false);
      setAiHelpOpen(true);
    }
  }


  const { previewRelatedArticle, refreshGapInboxCount, refreshGapInbox } = useKbGapInboxData({
    gapInboxTestingOnly,
    gapInboxPage,
    gapInboxPageSize,
    setGapInboxLoading,
    setGapInboxOpenCount,
    setGapInboxListTotal,
    setGapInboxItems,
    setRelatedPreviewLoading,
    setRelatedPreview,
    showNote,
    showAiFeedback,
  });

  const { refreshDraftCount, refreshPendingProposalCount, loadDraftsIntoResults, doSearch, doChunkAudit } = useKbLibraryWorkflow({
    q,
    searchAudience,
    searchScope,
    searchDocType,
    searchStatus,
    quickAuditRisk,
    canUseQuickChunkAudit,
    canPublishKb,
    setDraftCount,
    setPendingProposalCount,
    setDraftsLoading,
    setResults,
    setSearchStatus,
    showNote,
  });

  useKbAdminPanelEffects({
    canPublishKb,
    articleStatus,
    setArticleStatus,
    loadedArticle,
    articleScope,
    canManageAppScope,
    canManageManufacturerScope,
    setArticleScope,
    note,
    setNotesOpen,
    articleSlug,
    articleContent,
    articleTitle,
    defaultArticleTitle,
    setArticleTitle,
    hasKbAccess,
    refreshDraftCount,
    refreshGapInboxCount,
    refreshPendingProposalCount,
    entrySubModeIntent: props.entrySubModeIntent,
    setKbMode,
    setGapInboxPage,
    refreshGapInbox,
    gapInboxStatusFilter,
    onEntrySubModeIntentConsumed: props.onEntrySubModeIntentConsumed,
  });

  const { resetAiSession, mergeAiQuestions, updateAiQuestionAnswer, setAiQuestionDisposition, applyAiBodyDraft, restorePreviousBody, applyDictationToPurpose } = useKbAiSessionHelpers({ dictation, aiBodyDraft, articleContent, roleSetters: { setAiPurpose, setAiGeneratedNotes, setAiResolvedFacts, setAiPlacementSummary, setAiSuggestedAction, setAiTopicComplexity, setAiRecommendedDocCount, setAiStopReason, setAiFollowOnDocs, setAiKbGapCandidates, setAiArticleLinkSuggestions, setAiRelatedArticles, setAiQuestionAnswers, setAiBodyDraft, setAiBodyGenerationNotes, setPreviousArticleContent, setRelatedPreview, setRelatedPreviewLoading, setAiLinksOpen, setAiGapsOpen, setAppliedSuggestion, setAiFeedback, setArticleContent }, previousArticleContent, showAiFeedback });

  const { loadArticle, devRescopeFromLibraryResult, devRescopeFromEditor, startNewArticle, clearWorkspace, resetSearchFilters, loadStarterCompanyDocs } = useKbEditorWorkflow({ canPublishKb, canManageAppScope, canManageManufacturerScope, defaultWriteScope, onNavigateToDevKbRescope: props.onNavigateToDevKbRescope, dictation, state: { setSelected, setArticleSlug, setLoadedArticle, setArticleTitle, setArticleContent, setArticleAudience, setArticleScope, setArticleDocType, setArticleTagsInput, setArticleSourceUrl, setArticleStatus, setChunkQuality, setChunkQualityAdvice, setDevRescopeBusy, loadedArticle, setLastArticleId, setAiAudienceHint, setAiScopeHint, setAiDocTypeHint, setAiHelpOpen, setSplitProposals, setSplitOpen, setSplitWorking, setSplitCreating, setBulkImportText, setConfirmDeleteSlug, setPreserveOriginalBody, setAiUpdateTitle, setAiUpdateMetadata, setArticleMode, setQ, setSearchAudience, setSearchScope, setSearchDocType, setSearchStatus, setResults, setKbMode }, values: { results, articleSlug, articleTitle, articleContent, articleScope, articleStatus, articleAudience, articleDocType, defaultArticleTitle }, resetAiSession, refreshDraftCount, showNote });

  const { doArticle } = useKbArticleSaveWorkflow({ hasEditorName, broaderScopeLocked, orgId: props.orgId, articleMode, articleSlug, articleTitle, articleContent, articleAudience, articleScope, articleDocType, articleTagsInput, articleSourceUrl, articleStatus, editorName, loadedArticle, setChunkQuality, setChunkQualityAdvice, setLastArticleId, setAiHelpOpen, setSplitProposals, setSplitOpen, refreshPendingProposalCount, refreshDraftCount, resetEditorAfterSave: startNewArticle, showNote });

  const { runAiHelp } = useKbAiHelpWorkflow({
    state: {
      articleContent, aiPurpose, aiAudienceHint, aiScopeHint, aiDocTypeHint, aiSourceKind, articleSlug, articleTitle, articleDocType, articleScope,
      articleTagsInput, aiQuestionAnswers, aiResolvedFacts, preserveOriginalBody, aiUpdateTitle, aiUpdateMetadata, setAiBusy, setArticleTitle, setArticleAudience,
      setArticleScope, setArticleDocType, setArticleStatus, setArticleTagsInput, setAiGeneratedNotes, setAiResolvedFacts, setAiPlacementSummary, setAiSuggestedAction,
      setAiTopicComplexity, setAiRecommendedDocCount, setAiStopReason, setAiFollowOnDocs, setAiKbGapCandidates, setAiArticleLinkSuggestions, setAiRelatedArticles,
      setAiBodyDraft, setAiBodyGenerationNotes, setAiLinksOpen, setAppliedSuggestion,
    },
    capabilities: { canManageManufacturerScope, canManageAppScope, canPublishKb },
    context: { defaultArticleTitle, kbOrgDisplayName, mergeAiQuestions, showNote, showAiFeedback },
  });

  const { doProposeSplit, doCreateSplitStubs, doBulkImport, onBulkImportFilePicked } = useKbImportAndSplitWorkflow({ articleContent, articleTitle, articleDocType, splitProposals, hasEditorName, editorName, articleAudience, articleScope, articleStatus, articleSlug, bulkImportText, bulkAllowPolicyOverwrite, canPublishKb, setAiHelpOpen, setSplitWorking, setSplitOpen, setSplitProposals, setSplitCreating, setIsBulkImporting, setBulkImportText, setKbMode, refreshDraftCount, showNote });

  const { doSoftDelete, doPublishDraft, doPurge, loadTrash } = useKbTrashAndPublishWorkflow({ articleSlug, loadedArticle, setDeleteWorking, setConfirmDeleteSlug, setConfirmPublishSlug, setConfirmPurgeSlug, setResults, setLoadedArticle, setArticleStatus, setKbMode, setArticleSlug, setTrashResults, setTrashLoading, setPublishWorking, setChunkQuality, setChunkQualityAdvice, refreshDraftCount, showNote });


  function logoutLegacyAdmin() {
    props.onLogout?.();
  }

  const { saveGapsToInbox, updateGapStatus, addressGapItem, suggestGapSolution, resolveGapByLink } = useKbGapInboxWorkflow({ aiKbGapCandidates, aiArticleLinkSuggestions, editorName, articleSlug, showAiFeedback, showNote, setGapSaving, refreshGapInboxCount, setGapInboxItems, refreshGapInbox, gapInboxStatusFilter, gapInboxTestingOnly, gapInboxPage, defaultWriteScope, canPublishKb, setArticleTitle, setArticleContent, setArticleScope, setArticleAudience, setArticleDocType, setArticleTagsInput, setArticleSourceUrl, setArticleStatus, setLoadedArticle, setArticleSlug, setLastArticleId, setSelected, resetAiSession, setKbMode });

  if (!hasKbAccess) {
    return (
      <div className="rounded-2xl border border-gray-200 bg-white p-6 text-left space-y-2 dark:border-zinc-700 dark:bg-zinc-900/45">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-zinc-100">Knowledge Base</h2>
        <p className="text-sm text-gray-700 dark:text-zinc-300">Knowledge Base access is not available for this account.</p>
        <p className="text-sm text-gray-600 dark:text-zinc-400">Sign in with a trainer-or-higher account, or use the legacy Knowledge Base admin login if you still need it.</p>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="mb-2 flex items-center gap-3">
        <span className="text-xs text-gray-500 dark:text-zinc-400">Org:</span>
        <span className="text-sm font-semibold text-indigo-700 dark:text-indigo-300">
          {kbOrgDisplayName}
        </span>
        {props.orgId && <span className="text-xs text-gray-400 dark:text-zinc-500">({props.orgId})</span>}
      </div>
      <div className="space-y-4 text-left">

        <KbAdminWorkspaceChrome
          kbMode={kbMode}
          setKbMode={setKbMode}
          gapInboxOpenCount={gapInboxOpenCount}
          pendingProposalCount={pendingProposalCount}
          refreshGapInbox={refreshGapInbox}
          canPublishKb={canPublishKb}
          editorName={editorName}
          draftCount={draftCount}
          loadedArticle={loadedArticle}
          startNewArticle={startNewArticle}
          clearWorkspace={clearWorkspace}
          onBulkImportFilePicked={onBulkImportFilePicked}
          hasLegacyAdminToken={hasLegacyAdminToken}
          onLogout={props.onLogout}
          logoutLegacyAdmin={logoutLegacyAdmin}
          aiGuidance={aiGuidance}
          onOpenTrash={loadTrash}
        />

        <KbAdminModes
          {...buildKbAdminModesProps({
            props,
            constants: { STARTER_COMPANY_DOCS_URL },
            dictation,
            state: {
              q, setQ, results, setResults, selected, setSelected, note, setNote, notesOpen, setNotesOpen, articleTitle, setArticleTitle, articleContent, setArticleContent, articleMode, setArticleMode, lastArticleId, setLastArticleId, articleSlug, setArticleSlug, loadedArticle, setLoadedArticle, articleAudience, setArticleAudience, articleScope, setArticleScope, articleDocType, setArticleDocType, articleTagsInput, setArticleTagsInput, articleSourceUrl, setArticleSourceUrl, articleStatus, setArticleStatus, searchAudience, setSearchAudience, searchScope, setSearchScope, searchDocType, setSearchDocType, searchStatus, setSearchStatus, quickAuditEnabled, setQuickAuditEnabled, quickAuditRisk, setQuickAuditRisk, chunkQuality, setChunkQuality, chunkQualityAdvice, setChunkQualityAdvice, bulkImportText, setBulkImportText, isBulkImporting, setIsBulkImporting, devRescopeBusy, setDevRescopeBusy, draftCount, setDraftCount, draftsLoading, setDraftsLoading, aiHelpOpen, setAiHelpOpen, aiPurpose, setAiPurpose, aiAudienceHint, setAiAudienceHint, aiScopeHint, setAiScopeHint, aiDocTypeHint, setAiDocTypeHint, aiSourceKind, setAiSourceKind, aiBusy, setAiBusy, aiGeneratedNotes, setAiGeneratedNotes, appliedSuggestion, setAppliedSuggestion, aiFeedback, setAiFeedback, aiResolvedFacts, setAiResolvedFacts, aiPlacementSummary, setAiPlacementSummary, aiSuggestedAction, setAiSuggestedAction, aiTopicComplexity, setAiTopicComplexity, aiRecommendedDocCount, setAiRecommendedDocCount, aiStopReason, setAiStopReason, aiFollowOnDocs, setAiFollowOnDocs, aiKbGapCandidates, setAiKbGapCandidates, aiArticleLinkSuggestions, setAiArticleLinkSuggestions, aiRelatedArticles, setAiRelatedArticles, aiQuestionAnswers, setAiQuestionAnswers, aiBodyDraft, setAiBodyDraft, aiBodyGenerationNotes, setAiBodyGenerationNotes, previousArticleContent, setPreviousArticleContent, preserveOriginalBody, setPreserveOriginalBody, aiUpdateTitle, setAiUpdateTitle, aiUpdateMetadata, setAiUpdateMetadata, relatedPreviewLoading, setRelatedPreviewLoading, relatedPreview, setRelatedPreview, aiLinksOpen, setAiLinksOpen, aiGapsOpen, setAiGapsOpen, splitProposals, setSplitProposals, splitWorking, setSplitWorking, splitCreating, setSplitCreating, splitOpen, setSplitOpen, gapInboxItems, setGapInboxItems, gapInboxOpenCount, setGapInboxOpenCount, gapInboxLoading, setGapInboxLoading, gapSaving, setGapSaving, gapInboxStatusFilter, setGapInboxStatusFilter, gapInboxPageSize, setGapInboxPageSize, gapInboxPage, setGapInboxPage, gapInboxListTotal, setGapInboxListTotal, gapInboxTestingOnly, setGapInboxTestingOnly, kbMode, setKbMode, pendingProposalCount, setPendingProposalCount, bulkAllowPolicyOverwrite, setBulkAllowPolicyOverwrite, confirmDeleteSlug, setConfirmDeleteSlug, deleteWorking, setDeleteWorking, confirmPublishSlug, setConfirmPublishSlug, publishWorking, setPublishWorking, trashResults, setTrashResults, trashLoading, setTrashLoading, confirmPurgeSlug, setConfirmPurgeSlug,
            },
            handlers: {
              doChunkAudit, doSearch, resetSearchFilters, loadDraftsIntoResults, loadArticle, doPublishDraft, doSoftDelete, devRescopeFromLibraryResult, applyAiBodyDraft, applyDictationToPurpose, doArticle, doCreateSplitStubs, doProposeSplit, previewRelatedArticle, devRescopeFromEditor, clearWorkspace, resetAiSession, restorePreviousBody, runAiHelp, saveGapsToInbox, setAiQuestionDisposition, showAiFeedback, updateAiQuestionAnswer, showNote, refreshGapInbox, updateGapStatus, addressGapItem, suggestGapSolution, resolveGapByLink, loadStarterCompanyDocs, doBulkImport, loadTrash, doPurge,
            },
            meta: {
              canPublishKb, canUseQuickChunkAudit, showDevRescopePill, aiGuidance, hasEditorName, publishedArticleLocked, broaderScopeLocked, canManageManufacturerScope, canManageAppScope, defaultArticleTitle, editorName,
            },
          })}
        />
      </div>
    </div>
  );
}